/**
 * Promotions Page
 * 
 * หน้ารายการโปรโมชั่น - ออกแบบตาม UX/UI pattern ของระบบ React app
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { 
  Search, 
  Plus, 
  RefreshCw, 
  Gift,
  Edit,
  Trash2,
  MoreVertical,
  Calendar,
  DollarSign,
  Percent,
  Sparkles,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  AlertTriangle,
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useAuth } from '@/components/auth/auth-provider'
import { PromotionService, type Promotion, type PromotionQueryParams, PromotionType, PromotionStatus } from '@/domain/promotion'
import { PromotionDialog } from './components/promotion-dialog'
import { cn } from '@/lib/utils'
import moment from 'moment'

// ============================================
// Helper Functions
// ============================================

function formatDate(date?: Date | string): string {
  if (!date) return '-'
  
  try {
    const d = date instanceof Date ? date : new Date(date)
    return moment(d).format('DD/MM/YYYY HH:mm')
  } catch {
    return '-'
  }
}

function formatNumber(value: number, decimals: number = 0): string {
  return value.toLocaleString('th-TH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function getPromotionTypeBadge(type: PromotionType) {
  switch (type) {
    case PromotionType.DEPOSIT_BONUS:
      return (
        <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30">
          ฝากเงินตามเงื่อนไข
        </Badge>
      )
    case PromotionType.MINI_GAME:
      return (
        <Badge className="bg-purple-500/15 text-purple-600 border-purple-500/30">
          Mini Game
        </Badge>
      )
    case PromotionType.CREDIT_GIFT:
      return (
        <Badge className="bg-green-500/15 text-green-600 border-green-500/30">
          แจกเครดิต
        </Badge>
      )
    default:
      return (
        <Badge variant="secondary">ไม่ทราบ</Badge>
      )
  }
}

function getBonusText(promotion: Promotion): string {
  if (promotion.promotion_type === PromotionType.MINI_GAME) {
    if (promotion.rewards && promotion.rewards.length > 0) {
      const min = Math.min(...promotion.rewards.map(r => r.bonus))
      const max = Math.max(...promotion.rewards.map(r => r.bonus))
      return `฿${formatNumber(min)} - ฿${formatNumber(max)}`
    }
    return 'สุ่มรางวัล'
  }
  
  if (promotion.type === 'fixed') {
    return `฿${formatNumber(promotion.amount || 0, 0)}`
  }
  
  if (promotion.type === 'percentage') {
    return `${promotion.amount || 0}%`
  }
  
  return '-'
}

// ============================================
// Stats Cards Component
// ============================================

function StatsCards({ 
  total, 
  active, 
  inactive,
  isLoading 
}: {
  total: number
  active: number
  inactive: number
  isLoading: boolean
}) {
  const stats = [
    {
      label: 'ทั้งหมด',
      value: total,
      icon: Gift,
      color: 'text-blue-600',
      bg: 'bg-blue-500/10',
    },
    {
      label: 'เปิดใช้งาน',
      value: active,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-500/10',
    },
    {
      label: 'ปิดใช้งาน',
      value: inactive,
      icon: XCircle,
      color: 'text-zinc-600',
      bg: 'bg-zinc-500/10',
    },
  ]
  
  return (
    <div className="grid grid-cols-3 gap-4">
      {stats.map((stat) => (
        <Card key={stat.label} className="border-zinc-200/50 dark:border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={cn("p-2.5 rounded-xl", stat.bg)}>
                <stat.icon className={cn("w-5 h-5", stat.color)} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{stat.label}</p>
                {isLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-lg font-bold mt-0.5">{stat.value.toLocaleString()}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// ============================================
// Main Component
// ============================================

export function PromotionsPage() {
  const { sessionToken } = useAuth()
  
  // State
  const [promotions, setPromotions] = useState<Promotion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [total, setTotal] = useState(0)
  
  // Dialog state
  const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  
  // Status toggle state
  const [statusToggleTarget, setStatusToggleTarget] = useState<{promotion: Promotion; newStatus: PromotionStatus} | null>(null)
  const [isTogglingStatus, setIsTogglingStatus] = useState<string | null>(null)
  
  // Success dialog state
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Stats
  const stats = useMemo(() => {
    const active = promotions.filter(p => p.status === PromotionStatus.ACTIVE).length
    const inactive = promotions.filter(p => p.status === PromotionStatus.INACTIVE).length
    return { total, active, inactive }
  }, [promotions, total])

  // Load promotions
  const loadPromotions = useCallback(async () => {
    if (!sessionToken) return

    setIsLoading(true)
    setError(null)

    try {
      const params: PromotionQueryParams = {
        query: searchQuery || undefined,
        page,
        limit,
      }

      const result = await PromotionService.find(sessionToken, params)
      setPromotions(result.items)
      setTotal(result.total)
    } catch (err: any) {
      console.error('Failed to load promotions:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลโปรโมชั่นได้')
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, searchQuery, page, limit])

  useEffect(() => {
    loadPromotions()
  }, [loadPromotions])

  // Handlers
  const handleCreate = () => {
    setSelectedPromotion(null)
    setIsEditMode(false)
    setIsDialogOpen(true)
  }

  const handleEdit = (promotion: Promotion) => {
    setSelectedPromotion(promotion)
    setIsEditMode(true)
    setIsDialogOpen(true)
  }

  const handleDelete = async (promotion: Promotion) => {
    if (!confirm(`ต้องการลบโปรโมชั่น "${promotion.name}" (${promotion.code}) หรือไม่?`)) {
      return
    }

    try {
      // TODO: Implement delete
      await PromotionService.delete(sessionToken!, promotion.objectId)
      await loadPromotions()
    } catch (err: any) {
      alert(`ไม่สามารถลบโปรโมชั่นได้: ${err.message}`)
    }
  }

  const handleDialogClose = (saved: boolean) => {
    setIsDialogOpen(false)
    setSelectedPromotion(null)
    if (saved) {
      setSuccessMessage(isEditMode ? 'บันทึกการแก้ไขโปรโมชั่นเรียบร้อยแล้ว' : 'เพิ่มโปรโมชั่นใหม่เรียบร้อยแล้ว')
      loadPromotions()
    }
  }
  
  // Handle status toggle request
  const handleStatusToggleRequest = (promotion: Promotion) => {
    const newStatus = promotion.status === PromotionStatus.ACTIVE 
      ? PromotionStatus.INACTIVE 
      : PromotionStatus.ACTIVE
    setStatusToggleTarget({ promotion, newStatus })
  }
  
  // Confirm status toggle
  const handleStatusToggleConfirm = async () => {
    if (!statusToggleTarget || !sessionToken) return
    
    const { promotion, newStatus } = statusToggleTarget
    setIsTogglingStatus(promotion.objectId)
    
    try {
      await PromotionService.updateStatus(sessionToken, promotion.objectId, newStatus)
      // Update local state
      setPromotions(prev => prev.map(p => 
        p.objectId === promotion.objectId 
          ? { ...p, status: newStatus }
          : p
      ))
      setSuccessMessage(
        newStatus === PromotionStatus.ACTIVE 
          ? `เปิดใช้งานโปรโมชั่น "${promotion.name}" เรียบร้อยแล้ว`
          : `ปิดใช้งานโปรโมชั่น "${promotion.name}" เรียบร้อยแล้ว`
      )
    } catch (err: any) {
      console.error('Failed to update status:', err)
      alert(`ไม่สามารถเปลี่ยนสถานะได้: ${err.message}`)
    } finally {
      setIsTogglingStatus(null)
      setStatusToggleTarget(null)
    }
  }
  
  // Cancel status toggle
  const handleStatusToggleCancel = () => {
    setStatusToggleTarget(null)
  }

  const handleSearch = (value: string) => {
    setSearchQuery(value)
    setPage(1) // Reset to first page
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">จัดการโปรโมชั่น</h1>
          <p className="text-sm text-muted-foreground">
            ระบบจัดการโปรโมชั่นและโบนัสทั้งหมด • {total.toLocaleString()} รายการ
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadPromotions} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">เพิ่มโปรโมชั่น</span>
            <span className="sm:hidden">เพิ่ม</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <StatsCards 
        total={stats.total}
        active={stats.active}
        inactive={stats.inactive}
        isLoading={isLoading}
      />

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="ค้นหารหัสโปรโมชั่น..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="h-10 w-full rounded-lg border bg-muted/50 pl-9 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Error State */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border-zinc-200/50 dark:border-zinc-800">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : promotions.length === 0 ? (
            <div className="p-12 text-center">
              <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-sm text-muted-foreground">ไม่พบข้อมูลโปรโมชั่น</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>รหัส</TableHead>
                      <TableHead>ชื่อโปรโมชั่น</TableHead>
                      <TableHead>ประเภท</TableHead>
                      <TableHead>โบนัส</TableHead>
                      <TableHead>ระยะเวลา</TableHead>
                      <TableHead className="text-center">สถานะ</TableHead>
                      <TableHead className="text-right w-28"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promotions.map((promotion, index) => (
                      <TableRow key={promotion.objectId} className="hover:bg-muted/50">
                        <TableCell className="text-muted-foreground text-sm">
                          {(page - 1) * limit + index + 1}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                            {promotion.code}
                          </code>
                        </TableCell>
                        <TableCell className="font-medium">
                          {promotion.name}
                        </TableCell>
                        <TableCell>
                          {getPromotionTypeBadge(promotion.promotion_type)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {promotion.promotion_type === PromotionType.MINI_GAME ? (
                              <Sparkles className="w-4 h-4 text-purple-500" />
                            ) : promotion.type === 'percentage' ? (
                              <Percent className="w-4 h-4 text-green-500" />
                            ) : (
                              <DollarSign className="w-4 h-4 text-green-500" />
                            )}
                            <span className="text-sm font-medium">{getBonusText(promotion)}</span>
                          </div>
                          {promotion.deposit_turnover_bonus_x && promotion.deposit_turnover_bonus_x > 0 && (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              เทิร์น {promotion.deposit_turnover_bonus_x}x
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-xs">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            <div>
                              <div className="font-medium">{formatDate(promotion.startDate)}</div>
                              <div className="text-muted-foreground">ถึง {formatDate(promotion.endDate)}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Switch
                              checked={promotion.status === PromotionStatus.ACTIVE}
                              disabled={isTogglingStatus === promotion.objectId}
                              onCheckedChange={() => handleStatusToggleRequest(promotion)}
                            />
                            {isTogglingStatus === promotion.objectId && (
                              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => handleEdit(promotion)}>
                                <Edit className="h-4 w-4" />
                                แก้ไข
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="gap-2 text-red-500 cursor-pointer"
                                onClick={() => handleDelete(promotion)}
                              >
                                <Trash2 className="h-4 w-4" />
                                ลบ
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 p-4 bg-muted/30 rounded-lg border">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-muted-foreground">
                    แสดง {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} จาก {total.toLocaleString()} รายการ
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">แสดง</span>
                    <Select
                      value={String(limit)}
                      onValueChange={(v) => { setLimit(Number(v)); setPage(1) }}
                    >
                      <SelectTrigger className="w-[70px] h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground">รายการ</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1 || isLoading}
                  >
                    หน้าแรก
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 min-w-[100px] text-center">
                    หน้า {page} / {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || isLoading}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages || isLoading}
                  >
                    หน้าสุดท้าย
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Promotion Dialog */}
      <PromotionDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        promotion={selectedPromotion}
        isEditMode={isEditMode}
      />
      
      {/* Status Toggle Confirmation Dialog */}
      <AlertDialog open={!!statusToggleTarget} onOpenChange={() => setStatusToggleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              ยืนยันการเปลี่ยนสถานะ
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusToggleTarget && (
                <>
                  คุณต้องการ
                  <strong className={statusToggleTarget.newStatus === PromotionStatus.ACTIVE ? 'text-emerald-600' : 'text-zinc-600'}>
                    {statusToggleTarget.newStatus === PromotionStatus.ACTIVE ? ' เปิดใช้งาน ' : ' ปิดใช้งาน '}
                  </strong>
                  โปรโมชั่น "<strong>{statusToggleTarget.promotion.name}</strong>" ({statusToggleTarget.promotion.code}) หรือไม่?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleStatusToggleCancel}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleStatusToggleConfirm}
              className={statusToggleTarget?.newStatus === PromotionStatus.ACTIVE 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-zinc-600 hover:bg-zinc-700'
              }
            >
              {statusToggleTarget?.newStatus === PromotionStatus.ACTIVE ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Success Message Dialog */}
      <AlertDialog open={!!successMessage} onOpenChange={() => setSuccessMessage(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              ดำเนินการสำเร็จ
            </AlertDialogTitle>
            <AlertDialogDescription>
              {successMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSuccessMessage(null)}>
              ตกลง
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
